import React, { useEffect, useMemo, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Form, Modal, Button, Spinner } from "react-bootstrap";
import api from "../../api";
import { toast } from "react-toastify";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import EmptyState from "../../components/common/EmptyState";
import { formatDateOrDash } from "./projectWorkspaceUtils";

const PRIORITY_VALUES = ["low", "normal", "high"];

// Kanban board -- columns/cards from GET /projects/:id/board (server
// precomputes columnKey/overdue). Drag-and-drop reuses the EXACT SAME
// status-transition API the rest of this app already uses for ticket
// status changes (GET /tasks/:id/allowed-transitions, POST /tasks/:id/
// transitions/:transition_id -- see pages/tasks/InlineStatusPicker.jsx and
// TaskDetailPage.jsx). A drop is only ever committed if the backend's
// workflow engine actually lists a transition into the target column's
// status right now; otherwise the card snaps back and a toast explains
// why. This never bypasses that check with an optimistic-always-succeeds
// update.
export default function ProjectWorkspaceBoardTab({ projectId }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [phases, setPhases] = useState([]);
  const [members, setMembers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);

  const [phaseFilter, setPhaseFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const [myTasksOnly, setMyTasksOnly] = useState(false);

  const [remarkPrompt, setRemarkPrompt] = useState(null); // { card, transition, targetColumnKey, previousColumnKey }
  const [remarkText, setRemarkText] = useState("");
  const [submittingRemark, setSubmittingRemark] = useState(false);

  useEffect(() => {
    api.get(`/projects/${projectId}/phases`, { params: { pageSize: 200 } })
      .then((res) => setPhases(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setPhases([]));
    api.get(`/projects/${projectId}/members`, { params: { pageSize: 200 } })
      .then((res) => setMembers(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setMembers([]));
    api.get("/task-types", { params: { is_active: 1, pageSize: 200 } })
      .then((res) => setTaskTypes(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setTaskTypes([]));
  }, [projectId]);

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get(`/projects/${projectId}/board`, {
        params: {
          project_phase_id: phaseFilter || undefined,
          assigned_to: assigneeFilter || undefined,
          priority: priorityFilter || undefined,
          task_type_id: taskTypeFilter || undefined,
          search: search || undefined,
          my_tasks: myTasksOnly ? 1 : undefined,
        },
      })
      .then((res) => setBoard(res.data?.data || { columns: [], cards: [] }))
      .catch((err) => setError(err.response?.data?.message || "Failed to load board"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const handle = setTimeout(load, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, phaseFilter, assigneeFilter, priorityFilter, taskTypeFilter, search, myTasksOnly]);

  const cardsByColumn = useMemo(() => {
    const map = {};
    (board?.columns || []).forEach((c) => { map[c.key] = []; });
    (board?.cards || []).forEach((card) => {
      if (!map[card.columnKey]) map[card.columnKey] = [];
      map[card.columnKey].push(card);
    });
    return map;
  }, [board]);

  const moveCardLocally = (cardId, toColumnKey) => {
    setBoard((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        cards: prev.cards.map((c) => (c.id === cardId ? { ...c, columnKey: toColumnKey } : c)),
      };
    });
  };

  const executeTransition = async (card, transition, remark) => {
    try {
      await api.post(`/tasks/${card.id}/transitions/${transition.transition_id}`, {
        remark: remark?.trim() || undefined,
      });
      toast.success("Status updated");
      return true;
    } catch (err) {
      const data = err?.response?.data;
      toast.error(data?.message || "Failed to update status");
      return false;
    }
  };

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const cardId = Number(String(draggableId).replace("card-", ""));
    const card = (board?.cards || []).find((c) => c.id === cardId);
    const targetColumn = (board?.columns || []).find((c) => c.key === destination.droppableId);
    if (!card || !targetColumn) return;

    const previousColumnKey = card.columnKey;
    // Optimistic move -- reverted below if the transition isn't allowed or fails.
    moveCardLocally(cardId, targetColumn.key);

    let transitions = [];
    try {
      const { data } = await api.get(`/tasks/${cardId}/allowed-transitions`);
      transitions = Array.isArray(data?.data) ? data.data : [];
    } catch {
      transitions = [];
    }

    const matched = transitions.find((t) => {
      const toStatus = t.to_status || {};
      return (
        String(toStatus.code || "") === String(targetColumn.key) ||
        String(toStatus.id || "") === String(targetColumn.key) ||
        String(toStatus.name || "").toLowerCase() === String(targetColumn.label || "").toLowerCase()
      );
    });

    if (!matched) {
      moveCardLocally(cardId, previousColumnKey);
      toast.error(`This status change ("${targetColumn.label}") isn't allowed from this task's current workflow.`);
      return;
    }

    if (matched.requires_remark) {
      // Needs a remark before it can be confirmed -- keep the optimistic
      // move visible while the prompt is open, revert if cancelled.
      setRemarkPrompt({ card, transition: matched, targetColumnKey: targetColumn.key, previousColumnKey });
      setRemarkText("");
      return;
    }

    const ok = await executeTransition(card, matched, "");
    if (!ok) moveCardLocally(cardId, previousColumnKey);
  };

  const cancelRemarkPrompt = () => {
    if (remarkPrompt) moveCardLocally(remarkPrompt.card.id, remarkPrompt.previousColumnKey);
    setRemarkPrompt(null);
    setRemarkText("");
  };

  const confirmRemarkPrompt = async () => {
    if (!remarkText.trim()) {
      toast.warning("A remark is required for this status change");
      return;
    }
    setSubmittingRemark(true);
    const ok = await executeTransition(remarkPrompt.card, remarkPrompt.transition, remarkText);
    setSubmittingRemark(false);
    if (!ok) moveCardLocally(remarkPrompt.card.id, remarkPrompt.previousColumnKey);
    setRemarkPrompt(null);
    setRemarkText("");
  };

  if (loading && !board) return <LoadingState message="Loading board…" />;
  if (error) return <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />;
  if (!board || board.columns.length === 0) {
    return <EmptyState title="No workflow configured" message="This project has no board columns yet." />;
  }

  return (
    <div>
      <div className="ui-filter-toolbar mb-3 d-flex flex-wrap gap-2">
        <Form.Control
          style={{ maxWidth: 220 }}
          placeholder="Search cards…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Form.Select style={{ maxWidth: 170 }} value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
          <option value="">All Phases</option>
          {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Form.Select>
        <Form.Select style={{ maxWidth: 190 }} value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
          <option value="">All Assignees</option>
          {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.user_name}</option>)}
        </Form.Select>
        <Form.Select style={{ maxWidth: 140 }} value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option value="">All Priorities</option>
          {PRIORITY_VALUES.map((p) => <option key={p} value={p}>{p}</option>)}
        </Form.Select>
        <Form.Select style={{ maxWidth: 170 }} value={taskTypeFilter} onChange={(e) => setTaskTypeFilter(e.target.value)}>
          <option value="">All Task Types</option>
          {taskTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Form.Select>
        <Form.Check
          type="switch"
          id="pw-my-tasks-toggle"
          label="My Tasks"
          checked={myTasksOnly}
          onChange={(e) => setMyTasksOnly(e.target.checked)}
          className="d-flex align-items-center"
        />
      </div>

      <div className="pw-board-scroll">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="pw-board-columns">
            {board.columns.map((col) => {
              const cards = cardsByColumn[col.key] || [];
              return (
                <div className="pw-board-column" key={col.key}>
                  <div className="pw-board-column-header">
                    <span>{col.label}</span>
                    <span className="pw-board-column-count">{cards.length}</span>
                  </div>
                  <Droppable droppableId={col.key}>
                    {(provided) => (
                      <div className="pw-board-cards" ref={provided.innerRef} {...provided.droppableProps}>
                        {cards.map((card, index) => (
                          <Draggable key={card.id} draggableId={`card-${card.id}`} index={index}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                className={`pw-board-card${dragSnapshot.isDragging ? " pw-board-card-dragging" : ""}${card.overdue ? " pw-board-card-overdue" : ""}`}
                              >
                                <div className="pw-board-card-title">{card.title}</div>
                                <div className="pw-board-card-meta">
                                  {card.priority && <span className="pw-chip pw-chip-secondary">{card.priority}</span>}
                                  {card.phase_name && <span className="pw-chip pw-chip-secondary">{card.phase_name}</span>}
                                  {card.assignee_name && <span><i className="fa-regular fa-user me-1" aria-hidden="true"></i>{card.assignee_name}</span>}
                                  {card.due_at && <span>Due {formatDateOrDash(card.due_at)}</span>}
                                  {card.overdue && <span className="text-danger fw-semibold">Overdue</span>}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      </div>

      <Modal show={!!remarkPrompt} onHide={cancelRemarkPrompt} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remark Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Moving to <strong>{remarkPrompt?.transition?.to_status?.name}</strong> requires a remark.
          </p>
          <Form.Control
            as="textarea"
            rows={3}
            autoFocus
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            placeholder="Add a remark…"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={cancelRemarkPrompt} disabled={submittingRemark}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={confirmRemarkPrompt} disabled={submittingRemark}>
            {submittingRemark ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
